import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useBadgeAwarder } from '@/hooks/useBadgeAwarder';
import { useUserCredits } from '@/hooks/useUserCredits';
import { toast } from 'sonner';
import type { AnnouncementFormData, TemplateType, RelationshipType } from '@/types/announcement';
import { DEFAULT_CUSTOM_SETTINGS } from '@/types/announcement';
import type { CourageModeData } from '@/types/courage';
import { moderateContent } from '@/types/courage';
import { getTryDraft, clearTryDraft } from '@/lib/tryDraft';

const WIZARD_ANNOUNCEMENT_SELECT = 'id, user_id, recipient_name, relationship, message, template, photo_urls, status, public_slug, created_at, updated_at, published_at, metadata, animation_theme, sender_display_name, is_anonymous, courage_mode, delivery_mode, reveal_policy, reveal_after_days, revealed_at';

const INITIAL_FORM_DATA: AnnouncementFormData = {
  recipientName: '',
  relationship: null,
  message: '',
  template: 'gratitude',
  photoUrls: [],
  photoFiles: [],
  customSettings: DEFAULT_CUSTOM_SETTINGS,
  animationTheme: 'elegant_floral',
};

type WizardAnnouncementRow = {
  id: string;
  user_id: string;
  recipient_name: string;
  relationship: RelationshipType | null;
  message: string;
  template: TemplateType;
  photo_urls: string[];
  status?: string | null;
  public_slug?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  published_at?: string | null;
  metadata: Record<string, any>;
  animation_theme: string;
  sender_display_name?: string | null;
  is_anonymous?: boolean | null;
  courage_mode?: string | null;
  delivery_mode?: string | null;
  reveal_policy?: string | null;
  reveal_after_days?: number | null;
  revealed_at?: string | null;
};

const TEMPLATE_VALUES: TemplateType[] = ['apology', 'gratitude', 'hard_conversation', 'love', 'closure', 'custom', 'birthday', 'anniversary', 'celebration', 'encouragement'];
const RELATIONSHIP_VALUES: RelationshipType[] = ['parent', 'grandparent', 'sibling', 'child', 'spouse', 'friend', 'mentor', 'colleague', 'other', 'crush'];
const ROUTE_DRAFT_ID_KEYS = ['draftId', 'draft_id', 'localDraftId', 'local_draft_id', 'originalDraftId', 'original_draft_id', 'sourceDraftId', 'source_draft_id', 'dashboardDraftId', 'dashboard_draft_id', 'announcementId', 'announcement_id'];

const asObject = (value: unknown): Record<string, any> => (
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, any> : {}
);

const asString = (value: unknown, fallback = ''): string => (
  typeof value === 'string' ? value : value == null ? fallback : String(value)
);

const normalizePhotoUrls = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter((url): url is string => typeof url === 'string' && url.length > 0);
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
};

const isTemporaryPhotoUrl = (url: string) => url.startsWith('blob:') || url.startsWith('data:');

const durablePhotoUrls = (value: unknown): string[] => (
  Array.from(new Set(normalizePhotoUrls(value).filter((url) => !isTemporaryPhotoUrl(url))))
);

const safeDraftErrorMessage = (error: any) => {
  const code = asString(error?.code || error?.status || error?.name, 'unknown');
  const message = asString(error?.message || error?.details || 'Unknown save error');
  return `Draft save failed: ${code} — ${message}`;
};

const unwrapRpcRows = (payload: unknown): any[] => {
  if (Array.isArray(payload)) return payload;
  const obj = asObject(payload);
  if (Array.isArray(obj.data)) return obj.data;
  if (Array.isArray(obj.rows)) return obj.rows;
  if (obj.data && typeof obj.data === 'object') return [obj.data];
  if (obj.announcement && typeof obj.announcement === 'object') return [obj.announcement];
  if (obj.id || obj.announcement_id) return [obj];
  return [];
};

const metadataIds = (metadata: Record<string, any>) => ROUTE_DRAFT_ID_KEYS.reduce<Record<string, string>>((acc, key) => {
  const value = metadata[key];
  if (typeof value === 'string' && value.trim()) acc[key] = value.trim();
  return acc;
}, {});

const candidateIdsForRow = (raw: any, normalized?: WizardAnnouncementRow | null): string[] => {
  const metadata = asObject(raw?.metadata ?? normalized?.metadata);
  const ids = [
    normalized?.id,
    raw?.id,
    raw?.announcement_id,
    raw?.announcementId,
    raw?.draft_id,
    raw?.draftId,
    raw?.saved_draft_id,
    raw?.savedDraftId,
    ...ROUTE_DRAFT_ID_KEYS.map((key) => metadata[key]),
  ];
  return Array.from(new Set(ids.map((id) => asString(id).trim()).filter(Boolean)));
};

const normalizeAnnouncementRow = (raw: any, currentUserId?: string): WizardAnnouncementRow | null => {
  const metadata = asObject(raw?.metadata);
  const id = asString(raw?.announcement_id ?? raw?.announcementId ?? raw?.id ?? metadata.announcementId ?? metadata.announcement_id).trim();
  if (!id) return null;

  const relationship = asString(raw?.relationship ?? metadata.relationship).trim() as RelationshipType;
  const template = asString(raw?.template ?? metadata.template, 'gratitude').trim() as TemplateType;

  return {
    ...raw,
    id,
    user_id: asString(raw?.user_id ?? raw?.userId ?? currentUserId),
    recipient_name: asString(raw?.recipient_name ?? raw?.recipientName ?? metadata.recipientName),
    relationship: RELATIONSHIP_VALUES.includes(relationship) ? relationship : null,
    message: asString(raw?.message ?? metadata.message),
    template: TEMPLATE_VALUES.includes(template) ? template : 'gratitude',
    photo_urls: durablePhotoUrls(raw?.photo_urls ?? raw?.photoUrls ?? metadata.photoUrls),
    metadata,
    animation_theme: asString(raw?.animation_theme ?? raw?.animationTheme ?? metadata.animationTheme, 'elegant_floral'),
  };
};

const summarizeRows = (rows: any[]) => rows.slice(0, 5).map((row) => ({
  id: row?.id ?? null,
  announcement_id: row?.announcement_id ?? row?.announcementId ?? null,
  draft_id: row?.draft_id ?? row?.draftId ?? null,
  metadataIds: metadataIds(asObject(row?.metadata)),
}));

const summarizeWizardObject = (row: WizardAnnouncementRow, wizardStep: number) => ({
  announcementId: row.id,
  userIdMatches: Boolean(row.user_id),
  relationship: row.relationship,
  template: row.template,
  status: row.status,
  photoCount: row.photo_urls.length,
  messageLength: row.message.length,
  wizardStep,
  metadataIds: metadataIds(row.metadata),
});

interface UseAnnouncementWizardOptions {
  announcementId?: string;
}

export function useAnnouncementWizard(options: UseAnnouncementWizardOptions = {}) {
  const { announcementId } = options;
  const { user, isHydrated } = useAuth();
  const { checkAnnouncementBadges } = useBadgeAwarder();
  const { credits, hasEnoughCredits, deductCredit } = useUserCredits();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<AnnouncementFormData>(INITIAL_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(!!announcementId);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const [existingPhotoUrls, setExistingPhotoUrls] = useState<string[]>([]);
  const [isFirstAnnouncement, setIsFirstAnnouncement] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasLoadedInitialDraft, setHasLoadedInitialDraft] = useState(!announcementId);
  const [resolvedAnnouncementId, setResolvedAnnouncementId] = useState<string | undefined>(announcementId);

  const totalSteps = 6;
  const isEditMode = !!announcementId;
  const editAnnouncementId = resolvedAnnouncementId || announcementId;

  // Local autosave: persists wizard state even if the screen sleeps, network drops,
  // or the DB draft load briefly fails on mobile. Keyed by user + announcement id.
  const autosaveKey = `sin_wizard_autosave_v2:${user?.id || 'anon'}:${announcementId || 'new'}`;
  const AUTOSAVE_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
  const hasMeaningfulDraftData = (data: Partial<AnnouncementFormData> | null | undefined) => {
    if (!data) return false;
    return Boolean(
      data.recipientName?.trim() ||
      data.message?.trim() ||
      data.relationship ||
      (Array.isArray(data.photoUrls) && data.photoUrls.length > 0)
    );
  };
  const persistDraftAutosave = (draftId: string, draftData: AnnouncementFormData, step: number) => {
    if (!user?.id || !hasMeaningfulDraftData(draftData)) return;
    try {
      const snapshot = {
        ts: Date.now(),
        currentStep: step,
        formData: { ...draftData, photoUrls: durablePhotoUrls(draftData.photoUrls), photoFiles: [] },
      };
      localStorage.setItem(`sin_wizard_autosave_v2:${user.id}:${draftId}`, JSON.stringify(snapshot));
      localStorage.removeItem(`sin_wizard_autosave_v2:${user.id}:new`);
    } catch { /* quota — ignore */ }
  };

  useEffect(() => {
    setResolvedAnnouncementId(announcementId);
    setHasLoadedInitialDraft(!announcementId);
  }, [announcementId]);

  // Restore draft from the unauthenticated "try" flow + apply optional
  // wizard prefs from the "Who is this for" pre-flow or occasion landing pages.
  useEffect(() => {
    if (announcementId) return; // don't override edit mode
    const draft = getTryDraft();
    let prefs: { relationship?: string; template?: string; animationTheme?: string } | null = null;
    try {
      const raw = sessionStorage.getItem('sin_wizard_prefs_v1');
      if (raw) prefs = JSON.parse(raw);
    } catch { /* noop */ }

    if (draft || prefs) {
      setFormData(prev => ({
        ...prev,
        recipientName: draft?.recipientName || prev.recipientName,
        message: draft?.message || prev.message,
        relationship: (prefs?.relationship as RelationshipType) || prev.relationship,
        template: (prefs?.template as TemplateType) || prev.template,
        animationTheme: prefs?.animationTheme || prev.animationTheme,
      }));
      if (draft) {
        clearTryDraft();
        try {
          Object.keys(localStorage)
            .filter((key) => key.startsWith('sin_wizard_autosave_v2:') && key.endsWith(':new'))
            .forEach((key) => localStorage.removeItem(key));
        } catch { /* noop */ }
      }
      if (draft) toast.success('Your message draft has been restored!');
    }
  }, [announcementId, autosaveKey]);


  // Load existing announcement data for editing — hardened against mobile
  // session-hydration races and transient network errors.
  useEffect(() => {
    if (!announcementId) return;
    // Wait for auth to fully hydrate before we attempt to read the row
    // (otherwise RLS rejects and we falsely show "Failed to load").
    if (!isHydrated) return;
    if (!user) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const loadAnnouncement = async () => {
      setIsLoading(true);
      setLoadError(null);

      const routeId = announcementId.trim();

      const loadByAnnouncementId = async (idToLoad: string) => {
        const rpc = await (supabase as any)
          .rpc('get_my_announcement_for_edit', { p_announcement_id: idToLoad });
        const rpcRows = unwrapRpcRows(rpc.data);
        const rpcRow = rpcRows.find((row) => row?.id === idToLoad || row?.announcement_id === idToLoad) || rpcRows[0];
        const normalizedRpcRow = normalizeAnnouncementRow(rpcRow, user.id);

        console.log('[Wizard] edit loader RPC response', {
          routeId,
          requestedId: idToLoad,
          rpcError: rpc.error?.message || null,
          rpcRows: summarizeRows(rpcRows),
          normalizedWizardObject: normalizedRpcRow ? summarizeWizardObject(normalizedRpcRow, Math.min(Math.max(Number(normalizedRpcRow.metadata.wizardStep) || 1, 1), totalSteps)) : null,
        });

        if (normalizedRpcRow) return { data: normalizedRpcRow, error: null, source: 'edit-rpc' };

        const direct = await supabase
          .from('announcements')
          .select(WIZARD_ANNOUNCEMENT_SELECT)
          .eq('id', idToLoad)
          .eq('user_id', user.id)
          .maybeSingle();
        const normalizedDirectRow = normalizeAnnouncementRow(direct.data, user.id);

        console.log('[Wizard] edit loader direct fallback response', {
          routeId,
          requestedId: idToLoad,
          directError: direct.error?.message || null,
          directRowId: direct.data?.id || null,
          normalizedWizardObject: normalizedDirectRow ? summarizeWizardObject(normalizedDirectRow, Math.min(Math.max(Number(normalizedDirectRow.metadata.wizardStep) || 1, 1), totalSteps)) : null,
        });

        return {
          data: normalizedDirectRow,
          error: normalizedDirectRow ? null : (rpc.error || direct.error),
          source: 'direct-id-fallback',
        };
      };

      const loadByDashboardDraftId = async () => {
        const dashboard = await (supabase as any).rpc('get_my_announcements_for_dashboard');
        let rows = unwrapRpcRows(dashboard.data);
        let directListError: any = null;

        if (dashboard.error || rows.length === 0) {
          const directList = await supabase
            .from('announcements')
            .select(WIZARD_ANNOUNCEMENT_SELECT)
            .eq('user_id', user.id)
            .order('updated_at', { ascending: false });
          directListError = directList.error;
          if (!directList.error && Array.isArray(directList.data)) rows = directList.data;
        }

        const normalizedRows = rows.map((row) => ({ raw: row, normalized: normalizeAnnouncementRow(row, user.id) }));
        const match = normalizedRows.find(({ raw, normalized }) => candidateIdsForRow(raw, normalized).includes(routeId));

        console.log('[Wizard] dashboard draft-id fallback response', {
          routeId,
          dashboardDraftId: routeId,
          dashboardError: dashboard.error?.message || null,
          directListError: directListError?.message || null,
          dashboardRows: summarizeRows(rows),
          matchedAnnouncementId: match?.normalized?.id || null,
          normalizedWizardObject: match?.normalized ? summarizeWizardObject(match.normalized, Math.min(Math.max(Number(match.normalized.metadata.wizardStep) || 1, 1), totalSteps)) : null,
        });

        if (match?.normalized) return { data: match.normalized, error: null, source: 'dashboard-draft-id-fallback' };
        return { data: null, error: dashboard.error || directListError, source: 'dashboard-draft-id-fallback' };
      };

      const fetchOnce = async () => {
        const byAnnouncementId = await loadByAnnouncementId(routeId);
        if (byAnnouncementId.data) return byAnnouncementId;

        const byDraftId = await loadByDashboardDraftId();
        if (byDraftId.data) return byDraftId;

        return {
          data: null,
          error: byDraftId.error || byAnnouncementId.error,
          source: 'not-found',
        };
      };

      // Backoff retries — survives slow mobile networks and edge cold starts.
      const delays = [0, 500, 1200, 2500];
      let data: WizardAnnouncementRow | null = null;
      let error: any = null;
      let source = 'not-started';
      for (const d of delays) {
        if (cancelled) return;
        if (d) await new Promise((r) => setTimeout(r, d));
        const res = await fetchOnce();
        data = res.data;
        error = res.error;
        source = res.source;
        if (data) break;
      }

      if (cancelled) return;

      if (data) {
        const metadata = data.metadata;
        const savedStep = Math.min(Math.max(Number(metadata.wizardStep) || 1, 1), totalSteps);
        const initialEditStep = 1;
        console.log('[Wizard] edit loader resolved', {
          routeId,
          dashboardDraftId: routeId === data.id ? null : routeId,
          announcementId: data.id,
          source,
          savedWizardStep: savedStep,
          normalizedWizardObject: summarizeWizardObject(data, initialEditStep),
        });
        setResolvedAnnouncementId(data.id);
        setFormData({
          recipientName: data.recipient_name,
          relationship: data.relationship as RelationshipType,
          message: data.message,
          template: data.template as TemplateType,
          photoUrls: data.photo_urls || [],
          photoFiles: [],
          customSettings: DEFAULT_CUSTOM_SETTINGS,
          metadata: metadata as any,
          animationTheme: (data as any).animation_theme || 'elegant_floral',
        });
        setCurrentStep(initialEditStep);
        setExistingPhotoUrls(data.photo_urls || []);
        setHasLoadedInitialDraft(true);
        setIsLoading(false);
        return;
      }

      // DB read failed — try the local autosave fallback before alerting the user
      // so they don't lose work after a sleep/timeout/refresh.
      try {
        const fallbackKeys = Array.from(new Set([
          autosaveKey,
          `sin_wizard_autosave_v2:${user.id}:${announcementId}`,
          `sin_wizard_autosave_v2:anon:${announcementId}`,
        ]));
        for (const key of fallbackKeys) {
          const raw = localStorage.getItem(key);
          if (!raw) continue;
          const parsed = JSON.parse(raw);
          if (parsed && Date.now() - (parsed.ts || 0) < AUTOSAVE_MAX_AGE_MS && hasMeaningfulDraftData(parsed.formData)) {
            const restoredPhotoUrls = durablePhotoUrls(parsed.formData.photoUrls);
            setFormData((prev) => ({ ...prev, ...parsed.formData, photoUrls: restoredPhotoUrls, photoFiles: [] }));
            if (typeof parsed.currentStep === 'number') setCurrentStep(Math.min(Math.max(parsed.currentStep, 1), totalSteps));
            setExistingPhotoUrls(restoredPhotoUrls);
            toast.message('Restored your unsaved progress');
            setHasLoadedInitialDraft(true);
            setIsLoading(false);
            return;
          }
        }
      } catch { /* noop */ }

      console.warn('[Wizard] Announcement not found after retry', { routeId, announcementId, resolvedAnnouncementId, error });
      setLoadError('We could not retrieve this saved draft yet. Please try again from your dashboard.');
      setIsLoading(false);
    };

    loadAnnouncement();
    return () => { cancelled = true; };
  }, [announcementId, user, isHydrated, autosaveKey]);

  // ===== Autosave =====
  // Restore on mount (new-draft mode) so a timed-out screen doesn't lose progress.
  const autosaveRestoredRef = useRef(false);
  useEffect(() => {
    if (announcementId) return; // edit mode handled inside loadAnnouncement
    if (!isHydrated || !user) return;
    if (autosaveRestoredRef.current) return;
    autosaveRestoredRef.current = true;
    try {
      const raw = localStorage.getItem(autosaveKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.formData) return;
      if (Date.now() - (parsed.ts || 0) > AUTOSAVE_MAX_AGE_MS) return;
      if (!hasMeaningfulDraftData(parsed.formData)) return;
      const restoredPhotoUrls = durablePhotoUrls(parsed.formData.photoUrls);
      // Never wipe photos the user attached in this session (session hydration
      // can finish late on mobile, after photos were already picked).
      setFormData((prev) => {
        if (prev.photoFiles.length > 0 || hasMeaningfulDraftData(prev)) return prev;
        return { ...prev, ...parsed.formData, photoUrls: restoredPhotoUrls, photoFiles: [] };
      });
      if (typeof parsed.currentStep === 'number') setCurrentStep(Math.min(Math.max(parsed.currentStep, 1), totalSteps));
      toast.message('Picked up where you left off');
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrated, user?.id]);

  // Persist locally (debounced) on every change — survives screen timeouts.
  useEffect(() => {
    if (!user) return;
    if (announcementId && (!hasLoadedInitialDraft || isLoading || loadError)) return;
    if (!hasMeaningfulDraftData(formData)) return;
    const t = setTimeout(() => {
      try {
        const snapshot = {
          ts: Date.now(),
          currentStep,
          formData: { ...formData, photoUrls: durablePhotoUrls(formData.photoUrls), photoFiles: [] },
        };
        localStorage.setItem(autosaveKey, JSON.stringify(snapshot));
      } catch { /* quota — ignore */ }
    }, 600);
    return () => clearTimeout(t);
  }, [formData, currentStep, user, autosaveKey, announcementId, hasLoadedInitialDraft, isLoading, loadError]);


  const updateFormData = useCallback((updates: Partial<AnnouncementFormData>) => {
    setFormData((prev) => {
      // Deep-merge metadata to prevent chibiUrl and other fields from being lost
      if (updates.metadata && prev.metadata) {
        return { ...prev, ...updates, metadata: { ...prev.metadata, ...updates.metadata } };
      }
      return { ...prev, ...updates };
    });
  }, []);

  const nextStep = useCallback(() => {
    const maxSteps = 6;
    if (currentStep < maxSteps) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback((step: number) => {
    const maxSteps = 6;
    if (step >= 1 && step <= maxSteps) {
      setCurrentStep(step);
    }
  }, []);

  const uploadPhotos = useCallback(async (): Promise<string[]> => {
    if (!user || formData.photoFiles.length === 0) return [];

    // Upload all files in parallel with a single retry per file. Moderation runs
    // fire-and-forget AFTER the URL is collected so a slow/cold edge function
    // never blocks the user's save.
    const uploadOne = async (file: File): Promise<string | null> => {
      const originalExt = (file.name.split('.').pop() || '').toLowerCase();
      const mimeExt = file.type.split('/')[1]?.toLowerCase() || '';
      const fileExt = originalExt || mimeExt || 'jpg';
      if (['heic', 'heif'].includes(fileExt) || ['image/heic', 'image/heif'].includes(file.type)) {
        toast.error('HEIC/HEIF photos are not supported yet. Please choose JPG, PNG, WebP, or GIF.');
        console.warn('[Wizard] Unsupported photo format rejected', { name: file.name, type: file.type, size: file.size });
        return null;
      }
      if (!['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(fileExt)) {
        toast.error(`Unsupported photo type: ${file.name || file.type || 'unknown file'}`);
        console.warn('[Wizard] Unsupported photo extension rejected', { name: file.name, type: file.type, size: file.size });
        return null;
      }
      const fileName = `${user.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      const attempt = () =>
        supabase.storage.from('announcement-photos').upload(fileName, file, {
          cacheControl: '3600',
          contentType: file.type || `image/${fileExt}`,
          upsert: false,
        });

      let { error: uploadError } = await attempt();
      if (uploadError) {
        // One retry — covers flaky mobile networks
        await new Promise((r) => setTimeout(r, 600));
        ({ error: uploadError } = await attempt());
      }

      if (uploadError) {
        console.error('[Wizard] Photo upload failed', {
          bucket: 'announcement-photos',
          path: fileName,
          name: file.name,
          type: file.type,
          size: file.size,
          error: uploadError,
        });
        toast.error(`Photo upload failed: ${uploadError.message || file.name}`);
        return null;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('announcement-photos')
        .getPublicUrl(fileName);

      // Fire-and-forget moderation — never blocks the save flow.
      supabase.functions
        .invoke('moderate-image', { body: { image_url: publicUrl } })
        .then(({ data: modResult }) => {
          if (modResult && modResult.safe === false) {
            supabase.storage.from('announcement-photos').remove([fileName]).catch(() => {});
            supabase.functions.invoke('notify-report', {
              body: {
                announcement_id: 'upload-phase',
                source: 'image_flag',
                flagged_category: modResult.category || 'inappropriate',
                reporter_details: modResult.reason || 'AI-flagged image',
                user_id: user.id,
                user_email: user.email,
                notify_user: true,
              },
            }).catch(() => {});
            toast.error(`Image "${file.name}" was flagged — check your email for details.`);
          }
        })
        .catch((modErr) => console.warn('Image moderation check skipped:', modErr));

      return publicUrl;
    };

    const results = await Promise.all(formData.photoFiles.map(uploadOne));
    return results.filter((u): u is string => !!u);
  }, [user, formData.photoFiles]);


  const saveAsDraft = useCallback(async () => {
    if (!user || !formData.relationship) {
      toast.error('Please complete the required fields');
      return null;
    }

    setIsSubmitting(true);

    try {
      const newPhotoUrls = await uploadPhotos();
      const allPhotoUrls = Array.from(new Set([...durablePhotoUrls(existingPhotoUrls), ...durablePhotoUrls(formData.photoUrls), ...newPhotoUrls]));

      if (isEditMode && editAnnouncementId) {
        const draftMetadata = { ...(formData.metadata || {}), wizardStep: currentStep };
        const { data, error } = await supabase
          .from('announcements')
          .update({
            recipient_name: formData.recipientName,
            relationship: formData.relationship,
            message: formData.message,
            template: formData.template as any,
            photo_urls: allPhotoUrls,
            status: 'draft',
            metadata: draftMetadata,
            animation_theme: formData.animationTheme,
          } as any)
          .eq('id', editAnnouncementId)
          .eq('user_id', user.id)
          .select()
          .single();

        if (error) throw error;
        const savedFormData = { ...formData, photoUrls: allPhotoUrls, photoFiles: [] };
        setExistingPhotoUrls(allPhotoUrls);
        setFormData(savedFormData);
        persistDraftAutosave(editAnnouncementId, savedFormData, currentStep);
        toast.success('Draft updated successfully!');
        return data;
      } else {
        const draftMetadata = { ...(formData.metadata || {}), wizardStep: currentStep };
        const { data, error } = await supabase
          .from('announcements')
          .insert({
            user_id: user.id,
            recipient_name: formData.recipientName,
            relationship: formData.relationship,
            message: formData.message,
            template: formData.template as any,
            photo_urls: allPhotoUrls,
            status: 'draft',
            metadata: draftMetadata,
            animation_theme: formData.animationTheme,
          } as any)
          .select()
          .single();

        if (error) throw error;
        const savedFormData = { ...formData, photoUrls: allPhotoUrls, photoFiles: [] };
        setExistingPhotoUrls(allPhotoUrls);
        setFormData(savedFormData);
        persistDraftAutosave(data.id, savedFormData, currentStep);
        
        const { count } = await supabase
          .from('announcements')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);
        
        const isFirst = count === 1;
        
        let earnedBadges: { badgeKey?: string }[] = [];
        if (formData.relationship) {
          earnedBadges = await checkAnnouncementBadges(formData.relationship, isFirst);
        }
        
        toast.success('Draft saved successfully!');
        return { ...data, earnedBadges };
      }
    } catch (error) {
      console.error('[Wizard] Draft save failed', {
        table: 'announcements',
        mode: isEditMode ? 'update' : 'insert',
        announcementId: editAnnouncementId || null,
        userIdPresent: Boolean(user?.id),
        relationship: formData.relationship,
        photoFiles: formData.photoFiles.map((file) => ({ name: file.name, type: file.type, size: file.size })),
        durablePhotoUrls: durablePhotoUrls(formData.photoUrls),
        error,
      });
      toast.error(safeDraftErrorMessage(error));
      return null;
    } finally {
      setIsSubmitting(false);
    }
  }, [user, formData, uploadPhotos, isEditMode, editAnnouncementId, existingPhotoUrls, checkAnnouncementBadges, currentStep]);

  const publishAnnouncement = useCallback(async (courageData?: CourageModeData) => {
    if (!user || !formData.relationship) {
      toast.error('Please complete all required fields');
      return null;
    }

    if (!formData.recipientName.trim()) {
      toast.error('Please enter the recipient name');
      return null;
    }

    if (!formData.message.trim()) {
      toast.error('Please write a message');
      return null;
    }

    const modResult = moderateContent(formData.message);
    if (modResult.blocked) {
      const reason = modResult.reason || 'This message may violate our community guidelines.';
      const suggestion = modResult.suggestion || 'Please revise it.';
      const snippet = modResult.matchedSentence
        ? `\n\nFlagged: "${modResult.matchedSentence.slice(0, 140)}${modResult.matchedSentence.length > 140 ? '…' : ''}"`
        : '';
      toast.error(`${reason}${snippet}\n\n${suggestion}`, { duration: 8000 });
      return null;
    }

    if (!isEditMode && !hasEnoughCredits(1)) {
      toast.error('You need at least 1 Announcement credit to publish');
      return { needsCredits: true };
    }

    setIsSubmitting(true);

    try {
      // Validate attached video (if any). Only block on explicit 404/410 —
      // CORS/network errors must not silently prevent publishing.
      const videoUrlCheck = (formData.metadata as any)?.videoUrl as string | undefined;
      if (videoUrlCheck) {
        console.log('[publish] video attachment present', { videoUrlCheck });
        try {
          const resp = await fetch(videoUrlCheck, { method: 'HEAD' });
          console.log('[publish] video HEAD result', { status: resp.status, ok: resp.ok });
          if (resp.status === 404 || resp.status === 410) {
            toast.error('Your video upload didn\'t finish. Please record again before publishing.');
            setIsSubmitting(false);
            return null;
          }
        } catch (e) {
          // CORS/opaque failure — proceed; recipient page will render or show fallback.
          console.warn('[publish] video HEAD check failed (non-blocking)', e);
        }
      }

      const newPhotoUrls = await uploadPhotos();
      const allPhotoUrls = Array.from(new Set([...durablePhotoUrls(existingPhotoUrls), ...durablePhotoUrls(formData.photoUrls), ...newPhotoUrls]));

      // Guard: user attached photos but none made it to storage — never publish silently without them.
      const attachedCount = formData.photoUrls.length;
      if (attachedCount > 0 && allPhotoUrls.length === 0) {
        console.error('[publish] photos attached but none uploaded', { attachedCount, files: formData.photoFiles.length });
        toast.error("Your photos didn't upload. Please re-add them and try again.");
        setIsSubmitting(false);
        return null;
      }

      let watermarkStatus = true;
      try {
        const { data: creditData } = await supabase
          .from('user_credits')
          .select('has_purchased_bundle')
          .eq('user_id', user.id)
          .maybeSingle();
        if (creditData?.has_purchased_bundle) {
          watermarkStatus = false;
        }
      } catch (e) {
        console.warn('Could not check watermark status:', e);
      }

      const isCrush = formData.relationship === 'crush';
      const meta = formData.metadata || {};
      const senderMode = meta.senderDisplayMode || 'named';

      // If user chose the translated version, ensure we publish the translated message
      const metaAny = meta as Record<string, any>;
      const messageToPublish = (metaAny.sendVersion === 'translated' && metaAny.translatedMessage)
        ? metaAny.translatedMessage as string
        : formData.message;

      let senderFields: Record<string, any>;

      if (isCrush && courageData) {
        senderFields = {
          courage_mode: courageData.courageMode,
          delivery_mode: courageData.deliveryMode,
          is_anonymous: courageData.courageMode !== 'signed',
          reveal_policy: courageData.courageMode === 'reveal_when_ready' ? courageData.revealPolicy : 'never',
          reveal_after_days: courageData.revealPolicy === 'after_days' ? courageData.revealAfterDays : null,
          recipient_email: courageData.deliveryMode === 'email_relay' ? courageData.recipientEmail : null,
          sender_display_name: courageData.courageMode === 'signed' ? (user.user_metadata?.full_name || user.email) : null,
        };
      } else {
        const isAnon = senderMode === 'anonymous' || senderMode === 'confession';
        let displayName: string | null = null;
        if (senderMode === 'nickname') {
          displayName = meta.senderNickname?.trim() || null;
        } else if (senderMode === 'named') {
          displayName = meta.senderDisplayName?.trim() || user.user_metadata?.full_name || user.email || null;
        }
        senderFields = {
          is_anonymous: isAnon,
          sender_display_name: displayName,
        };
      }

      if (isEditMode && editAnnouncementId) {
        const { data: existingAnnouncement } = await supabase
          .from('announcements')
          .select('status')
          .eq('id', editAnnouncementId)
          .eq('user_id', user.id)
          .maybeSingle();

        const wasAlreadyPublished = existingAnnouncement?.status === 'approved';
        if (!wasAlreadyPublished && !hasEnoughCredits(1)) {
          toast.error('You need at least 1 Announcement credit to publish');
          return { needsCredits: true };
        }

        const { data, error } = await supabase
          .from('announcements')
          .update({
            recipient_name: formData.recipientName,
            relationship: formData.relationship,
            message: messageToPublish,
            template: formData.template as any,
            photo_urls: allPhotoUrls,
            status: 'approved',
            metadata: formData.metadata || {},
            has_watermark: watermarkStatus,
            animation_theme: formData.animationTheme,
            ...senderFields,
          } as any)
          .eq('id', editAnnouncementId)
          .eq('user_id', user.id)
          .select()
          .single();

        if (error) throw error;

        if (!wasAlreadyPublished) {
          const creditDeducted = await deductCredit('announcement_published');
          if (!creditDeducted) {
            console.warn('Failed to deduct credit after publishing draft announcement');
          }
        }

        toast.success('Announcement updated and published!');
        try { localStorage.removeItem(autosaveKey); } catch { /* noop */ }
        return data;
      } else {
        const { data, error } = await supabase
          .from('announcements')
          .insert({
            user_id: user.id,
            recipient_name: formData.recipientName,
            relationship: formData.relationship,
            message: messageToPublish,
            template: formData.template as any,
            photo_urls: allPhotoUrls,
            status: 'approved',
            metadata: formData.metadata || {},
            has_watermark: watermarkStatus,
            animation_theme: formData.animationTheme,
            ...senderFields,
          } as any)
          .select()
          .single();

        if (error) throw error;

        const creditDeducted = await deductCredit('announcement_published');
        if (!creditDeducted) {
          console.warn('Failed to deduct credit, but announcement was created');
        }
        
        const { count } = await supabase
          .from('announcements')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);
        
        const isFirst = count === 1;
        setIsFirstAnnouncement(isFirst);
        
        let earnedBadges: { badgeKey?: string }[] = [];
        if (formData.relationship) {
          earnedBadges = await checkAnnouncementBadges(formData.relationship, isFirst);
        }
        
        toast.success('Announcement published successfully!');
        try { localStorage.removeItem(autosaveKey); } catch { /* noop */ }
        return { ...data, isFirstAnnouncement: isFirst, earnedBadges };
      }
    } catch (error) {
      console.error('Publish error:', error);
      toast.error('Failed to publish announcement');
      return null;
    } finally {
      setIsSubmitting(false);
    }
  }, [user, formData, uploadPhotos, isEditMode, editAnnouncementId, existingPhotoUrls, checkAnnouncementBadges, hasEnoughCredits, deductCredit, autosaveKey]);

  const resetWizard = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setCurrentStep(1);
    setUploadedUrls([]);
    setExistingPhotoUrls([]);
  }, []);

  const removeExistingPhoto = useCallback((index: number) => {
    setExistingPhotoUrls((prev) => prev.filter((_, i) => i !== index));
    setFormData((prev) => ({
      ...prev,
      photoUrls: prev.photoUrls.filter((_, i) => i !== index),
    }));
  }, []);

  // Simplified 6-step flow:
  // 1. Combined Entry (Who + Relationship + Category)
  // 2. Message (Write)
  // 3. Identity/Courage
  // 4. Photos (optional)
  // 5. Preview
  // 6. Delivery
  const canProceed = useCallback((step: number, courageData?: CourageModeData): boolean => {
    const isCrush = formData.relationship === 'crush';
    
    if (isCrush) {
      switch (step) {
        case 1: return !!formData.relationship && !!formData.recipientName.trim() && !!formData.template;
        case 2: return !!formData.message.trim();
        case 3: {
          if (courageData?.deliveryMode === 'email_relay') {
            const email = courageData.recipientEmail?.trim() || '';
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
          }
          return true;
        }
        case 4: return true; // Photos optional
        case 5: return true; // Preview
        case 6: return true; // Delivery
        default: return false;
      }
    }

    switch (step) {
      case 1: return !!formData.relationship && !!formData.recipientName.trim() && !!formData.template;
      case 2: return !!formData.message.trim();
      case 3: return true; // Identity choice
      case 4: return true; // Photos optional
      case 5: return true; // Preview
      case 6: return true; // Delivery
      default: return false;
    }
  }, [formData]);

  return {
    currentStep,
    totalSteps,
    formData,
    isSubmitting,
    isLoading,
    loadError,
    isEditMode,
    existingPhotoUrls,
    updateFormData,
    nextStep,
    prevStep,
    goToStep,
    saveAsDraft,
    publishAnnouncement,
    resetWizard,
    canProceed,
    removeExistingPhoto,
  };
}
